import { X } from "lucide-react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { useEffect, useState } from "react"

/**
 * El saludo que aparece la primera vez, junto al botón de reportar.
 *
 * ⚠ **Existe porque un botón redondo sin texto no dice qué hace.** El salvavidas de
 * la esquina es reconocible una vez que sabes qué es; la primera vez es un icono
 * más. Quien tiene un fallo delante no se pone a explorar botones: sigue a mano y el
 * fallo no se entera nadie. Una frase, una vez, y ya sabe que ese sitio existe.
 *
 * ⚠ **Una vez y nunca más, y se recuerda por navegador.** Un saludo que reaparece
 * cada vez que se entra deja de ser una presentación y pasa a ser algo que hay que
 * cerrar todos los días. Se guarda en `localStorage`, que es exactamente el tipo de
 * comodidad por persona para el que sirve: si se pierde —otro navegador, datos
 * borrados—, lo peor que pasa es que se saluda otra vez.
 *
 * ⚠ **`localStorage` entre `try`.** En una ventana privada, o con las cookies de
 * sitio bloqueadas, el simple acceso LANZA. Sin el `try`, la excepción sube por el
 * render y el límite de error deja la pantalla en blanco: el saludo de soporte
 * habría tumbado la aplicación entera.
 *
 * ⚠ **Se retira solo a los ocho segundos.** Lo suficiente para leerlo sin que se
 * quede ahí tapando la esquina; y como no vuelve, no hace falta cerrarlo — el botón
 * de cerrar está igualmente, porque quien lo quiere fuera lo quiere fuera ya.
 */

const CLAVE = "setieca:soporte:saludado"

/** Cuánto tarda en asomar, y cuánto se queda. Parámetros y no constantes fijas:
 *  así los plazos se prueban en milisegundos en vez de esperar nueve segundos
 *  reales por prueba, sin tocar lo que ve nadie. */
export const ESPERA_MS = 1200
export const DURACION_MS = 8000

/** `true` si a esta persona no se le ha saludado todavía en este navegador. */
function faltaSaludar(): boolean {
  try {
    return window.localStorage.getItem(CLAVE) === null
  } catch {
    // Ventana privada o almacenamiento bloqueado: se saluda igual. Es preferible
    // repetir un saludo que caerse por no poder leer una preferencia cosmética.
    return true
  }
}

function darPorSaludado(): void {
  try {
    window.localStorage.setItem(CLAVE, "1")
  } catch {
    // Sin sitio donde recordarlo, se saludará otra vez. No es un fallo.
  }
}

export function SaludoDeSoporte({
  onAbrir,
  esperaMs = ESPERA_MS,
  duracionMs = DURACION_MS,
}: {
  onAbrir: () => void
  esperaMs?: number
  duracionMs?: number
}) {
  const sinMovimiento = useReducedMotion()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (!faltaSaludar()) return

    // ⚠ **No aparece de golpe al cargar.** Entrar a una pantalla y que algo salte
    // en la esquina en el mismo instante compite con lo que la persona venía a
    // hacer: se lee como un anuncio y se cierra sin mirarlo.
    const entrar = window.setTimeout(() => setVisible(true), esperaMs)
    const salir = window.setTimeout(() => setVisible(false), esperaMs + duracionMs)
    // Se marca al programarlo y no al cerrarlo: si alguien navega antes de que
    // aparezca, ya se le ha "gastado" el saludo. Reaparecer en la pantalla
    // siguiente sería justo lo que se quiere evitar.
    darPorSaludado()

    return () => {
      window.clearTimeout(entrar)
      window.clearTimeout(salir)
    }
  }, [esperaMs, duracionMs])

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="saludo-soporte"
          // No es `alert`: no ha pasado nada y no hay que interrumpir a nadie.
          role="status"
          className="fixed right-20 bottom-6 z-40 max-w-[16rem] rounded-[var(--radius-tarjeta)] border px-3 py-2 shadow-lg"
          style={{
            backgroundColor: "var(--surface-elevated)",
            borderColor: "var(--primary-borde)",
          }}
          // Sólo `transform` y `opacity`: nada de `width` ni `right`.
          initial={sinMovimiento ? false : { opacity: 0, x: 12, scale: 0.96 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={sinMovimiento ? { opacity: 0 } : { opacity: 0, x: 8, scale: 0.98 }}
          transition={
            sinMovimiento
              ? { duration: 0 }
              : { type: "spring", stiffness: 420, damping: 34, mass: 0.7 }
          }
        >
          <div className="flex items-start gap-2">
            <div className="flex-1">
              <p className="text-sm font-medium">¿Algo no funciona como esperabas?</p>
              <p className="mt-0.5 text-xs" style={{ color: "var(--fg-muted)" }}>
                Cuéntamelo desde aquí y llega a desarrollo con lo necesario para reproducirlo.
              </p>
              <button
                type="button"
                onClick={() => {
                  setVisible(false)
                  onAbrir()
                }}
                data-testid="saludo-reportar"
                className="mt-2 text-xs font-medium underline-offset-2 hover:underline"
                style={{ color: "var(--primary-texto)" }}
              >
                Reportar una falla
              </button>
            </div>
            <button
              type="button"
              onClick={() => setVisible(false)}
              data-testid="cerrar-saludo"
              aria-label="Cerrar el aviso"
              className="-mt-1 -mr-1 shrink-0 rounded-[var(--radius-control)] p-1"
              style={{ color: "var(--fg-muted)" }}
            >
              <X size={14} aria-hidden={true} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
